import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import Linkedin from '../../assets/linkedinB.svg';
import Users from '../../assets/users.svg';
import Email from '../../assets/email.svg';
import { VscVerifiedFilled } from "react-icons/vsc";
import id from '../../assets/id.svg';
import { Lead } from '../../hooks/useLeads';
import { usePeopleFetch } from '../../hooks/usePeopleFetch';
import { useProfilePicture } from '../../hooks/useProfilePicture';
import apiHandler from '../../services/apiServices';

interface DecisionMakerCardProps {
  data: {
    profile_pic_url?: string;
    first_name: string;
    last_name: string;
    designation: string;
    url?: string;
    email?: string;
    is_verified: boolean;
    email_status: "unfetched" | "fetching" | "fetched";
    op?: any;
  };
  className?: string;
  intentLeadId: string;
  isExpanded: boolean;
  onToggle: () => void;
}
const EmailIcon = () => <img src={Email} alt="email" className="w-[16px] h-[16px] text-[#7A7A7A]" />;
const LinkedinIcon = () => <img src={Linkedin} alt="linkedin" className="w-[16px] h-[16px] text-[#7A7A7A]" />;  
const UsersIcon = () => <img src={Users} alt="users" className="w-[16px] h-[16px] text-[#7A7A7A]" />;
const IdIcon = () => <img src={id} alt="id" className="w-[16px] h-[16px] text-[#7A7A7A]" />;

const DecisionMakerCard: React.FC<DecisionMakerCardProps> = ({ data, className, intentLeadId, isExpanded, onToggle }) => {
  const [showCopied, setShowCopied] = useState(false);
  const { mutate: fetchEmail, isPending: isFetchingEmail } = usePeopleFetch();
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout>();
  const queryClient = useQueryClient();

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleEmailClick = (e: React.MouseEvent, email: string) => {
    e.preventDefault();
    navigator.clipboard.writeText(email);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  };

  const { pictureUrl, loading, error } = useProfilePicture(
    data.first_name,
    data.last_name,
    data.url,
    data.profile_pic_url
  );

  const handleFetchEmail = async () => {
    try {
      if (!intentLeadId) {
        console.error('No lead ID provided');
        return;
      }
      
      setIsPolling(true);
      
      // Make the initial email fetch request
      await apiHandler.post(`/intent-leads/${intentLeadId}/email`, {
        first_name: data.first_name,
        last_name: data.last_name
      });

      // Function to check email status
      const checkEmailStatus = async () => {
        // Fetch lead directly by ID
        const response = await apiHandler.get(`/intent-leads/clubbed/${intentLeadId}`);
        const updatedLead = response.data;
        
        // Update only the individual lead cache
        queryClient.setQueryData(['intentLead', intentLeadId], updatedLead);

        const person = updatedLead?.company_data.people?.find(
          (p: Lead['company_data']['people'][0]) => 
            p.first_name === data.first_name && p.last_name === data.last_name
        );
        
        return {
          email_status: person?.email_status || null,
          email: person?.email,
          is_verified: person?.is_verified
        };
      };

      // Start polling for email status
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const status = await checkEmailStatus();
          
          // Only stop polling if status is explicitly "fetched"
          if (status.email_status === "fetched") {
            clearInterval(pollingIntervalRef.current);
            setIsPolling(false);
            
            // Get current cache data
            const cachedLead = queryClient.getQueryData(['intentLead', intentLeadId]) as Lead;
            if (cachedLead) {
              const updatedPeople = cachedLead.company_data.people.map(person => {
                if (person.first_name === data.first_name && person.last_name === data.last_name) {
                  return {
                    ...person,
                    email_status: status.email_status,
                    email: status.email,
                    is_verified: status.is_verified
                  };
                }
                return person;
              });

              // Update cache with new data
              queryClient.setQueryData(['intentLead', intentLeadId], {
                ...cachedLead,
                company_data: {
                  ...cachedLead.company_data,
                  people: updatedPeople
                }
              });
            }
          }
        } catch (error) {
          console.error('Polling error:', error);
          // Don't stop polling on error, let it continue
        }
      }, 5000);

    } catch (error) {
      console.error('Failed to fetch email:', error);
      setIsPolling(false);
    }
  };

  const renderProfilePicture = () => {
    const initials = getInitials(data.first_name, data.last_name);
    
    if (loading) {
      return (
        <div className="w-[24px] h-[24px] rounded-[4px] bg-[#F9FAFB] flex items-center justify-center border border-[#EEEEEE]">
          <Loader2 className="w-4 h-4 text-[#5D90D4] animate-spin" />
        </div>
      );
    }

    if (pictureUrl && !error) {
      return (
        <div className="relative w-[24px] h-[24px]">
          <img 
            src={pictureUrl}
            alt={`${data.first_name} ${data.last_name}`}
            className="w-full h-full rounded-[4px] object-cover border border-[#EEEEEE]"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                const fallback = document.createElement('div');
                fallback.className = "absolute inset-0 w-full h-full rounded-[4px] bg-blue-50 flex items-center justify-center border border-[#EEEEEE]";
                fallback.innerHTML = `<span class="text-[#5D90D4] text-sm font-semibold">${initials}</span>`;
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
      );
    }

    // Default fallback with initials
    return (
      <div className="w-[24px] h-[24px] rounded-[4px] bg-blue-50 flex items-center justify-center border border-[#EEEEEE]">
        <span className="text-[#5D90D4] text-sm font-semibold">
          {initials}
        </span>
      </div>
    );
  };

  const details = [
    {
      icon: IdIcon,
      label: "Name",
      value: (
        <div className="flex items-center gap-2">
          {renderProfilePicture()}
          <span>{data.first_name} {data.last_name}</span>
        </div>
      )
    },
    {
      icon: UsersIcon,
      label: "Position",
      value: data.designation
    },
    {
      icon: LinkedinIcon,
      label: "LinkedIn",
      value: data.url ? (
        <a 
          href={data.url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-[#5D90D4] hover:underline truncate"
        >
          View Profile
        </a>
      ) : 'N/A'
    },
    {
      icon: EmailIcon,
      label: "Email",
      value: (() => {
        // Case 1: When fetching (either from isFetchingEmail or email_status is "fetching" or isPolling)
        if (isFetchingEmail || data.email_status === "fetching" || isPolling) {
          return (
            <div className="flex items-center gap-2 text-[#7A7A7A]">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Fetching...</span>
            </div>
          );
        }

        // Case 2: When email is fetched
        if (data.email_status === "fetched") {
          // Case 2a: Email exists
          if (data.email) {
            return (
              <div className="relative flex items-center gap-2">
                <a 
                  href={`mailto:${data.email}`} 
                  onClick={(e) => handleEmailClick(e, data.email!)}
                  className="text-[#5D90D4] hover:underline truncate"
                >
                  {data.email}
                </a>
                {data.is_verified && (
                  <VscVerifiedFilled 
                    className="w-4 h-4 text-[#5D90D4]"
                  />
                )}
                {showCopied && (
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded">
                    Copied to clipboard
                  </div>
                )}
              </div>
            );
          }
          // Case 2b: No email found
          return (
            <span className="text-[#222222]">No email available</span>
          );
        }

        // Case 3: Initial state (unfetched) - Show fetch button
        return (
          <button 
            onClick={handleFetchEmail}
            disabled={(data.email_status as "unfetched" | "fetching" | "fetched") === "fetching" || isFetchingEmail}
            className="text-[#5D90D4] hover:underline truncate disabled:opacity-50"
          >
            {isFetchingEmail ? 'Fetching...' : 'Fetch Email'}
          </button>
        );
      })()
    }
  ];

  return (
    <div className={`flex flex-col items-start border-b border-[#EEEEEE] p-3 ${className}`}>
      <div className="flex items-center justify-between gap-2 w-full">
        <button 
          onClick={onToggle}
          className="flex items-center gap-2 rounded-lg"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-[#4B5563]" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[#4B5563]" />
          )}
          <h2 className="text-[14px] font-medium text-[#222222] pr-2">
            Decision maker
          </h2>
        </button>

        {/* <div className="flex items-center gap-3">
          <button 
            className="flex items-center gap-2 text-[#75777C] hover:text-[#222222] text-sm"
            onClick={() => {}}
          >
            <img src={reachout} alt="reachout" className="w-[14px] h-[14px] text-[#75777C]" />
            Reachout
          </button>
          <button 
            className="flex items-center gap-2 text-[#75777C] hover:text-[#222222] text-sm"
            onClick={() => {}}
          >
            <img src={activity} alt="activity" className="w-[14px] h-[14px] text-[#75777C]" />
            Activity
          </button>
        </div> */}
      </div>

      {isExpanded && (
        <table className="w-full mt-3">
          <tbody>
            {details.map((detail, index) => {
              const Icon = detail.icon;
              return (
                <tr key={index} className="group">
                  <td className="py-2 align-top w-[33%]">
                    <div className="flex items-center gap-2 text-sm font-[500px]  text-[#5C5E63]">
                      <Icon />
                      <span>{detail.label}</span>
                    </div>
                  </td>
                  <td className="py-2 align-top w-[67%] text-left">
                    <div className="text-sm font-[500px]  text-[#000000]">
                      {detail.value}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DecisionMakerCard; 